import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import axios from 'axios';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { formatDate, getLocalDateString } from '../../../utils/dateUtils';
import Swal from 'sweetalert2';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

const statusBadge = (status) => {
    switch (status) {
        case 'open': return <span className="badge bg-primary">Open</span>;
        case 'follow_up': return <span className="badge bg-warning text-dark">Follow-up</span>;
        case 'closed': return <span className="badge bg-secondary">Closed</span>;
        case 'converted': return <span className="badge bg-success">Converted</span>;
        default: return <span className="badge bg-info">{status}</span>;
    }
};

function MyLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
    const [filters, setFilters] = useState({ name: '', email: '', category: '', startDate: '', endDate: '', status: '' });

    const fetchLeads = async (page = 1) => {
        try {
            setLoading(true);
            const params = {
                page,
                limit: pagination.limit,
                name: filters.name,
                email: filters.email,
                category: filters.category,
                startDate: filters.startDate,
                endDate: filters.endDate,
                status: filters.status
            };
            const response = await apiClient.get('/vendors/leads', { params });
            if (response.data.success) {
                setLeads(response.data.data);
                setPagination(response.data.pagination);
            }
        } catch (err) {
            setError("Failed to fetch leads");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeads();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.category, filters.startDate, filters.endDate, filters.status]); // Re-fetch on category, date, or status change immediately

    // Debounce search filters
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (filters.name !== '' || filters.email !== '') {
                fetchLeads(1);
            }
        }, 500);

        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => clearTimeout(delayDebounceFn);
    }, [filters.name, filters.email]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchLeads(newPage);
        }
    };

    return (
        <div className="row">
            {/* <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 className="mb-sm-0">My Assigned Leads</h4>
                </div>
            </div> */}

            <div className="col-lg-12">
                <div className="card">
                    <div className="card-header border-0">
                        <div className="row g-4 align-items-center">
                            <div className="col-sm">
                                <div>
                                    <h5 className="card-title mb-0">Leads List</h5>
                                </div>
                            </div>
                            <div className="col-sm-auto">
                                <div className="d-flex flex-wrap align-items-start gap-2">
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control form-control-sm"
                                        placeholder="Search Name..."
                                        value={filters.name}
                                        onChange={handleFilterChange}
                                        style={{ width: '180px' }}
                                    />
                                    <input
                                        type="text"
                                        name="email"
                                        className="form-control form-control-sm"
                                        placeholder="Search Email..."
                                        value={filters.email}
                                        onChange={handleFilterChange}
                                        style={{ width: '180px' }}
                                    />
                                    <select
                                        name="category"
                                        className="form-select form-select-sm"
                                        value={filters.category}
                                        onChange={handleFilterChange}
                                        style={{ width: '150px' }}
                                    >
                                        <option value="">All Categories</option>
                                        <option value="holidays">Holidays</option>
                                        <option value="hotels">Hotels</option>
                                        <option value="cab">Cab</option>
                                        <option value="tirth_yatra">Tirth Yatra</option>
                                        <option value="medical_tourism">Medical Tourism</option>
                                    </select>
                                    <select
                                        className="form-select form-select-sm"
                                        value={filters.status}
                                        onChange={(e) => {
                                            setFilters(prev => ({ ...prev, status: e.target.value }));
                                        }}
                                        style={{ width: '150px' }}
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="open">Open</option>
                                        <option value="follow_up">Follow-up</option>
                                        <option value="closed">Closed</option>
                                        <option value="converted">Converted</option>
                                    </select>
                                    <Flatpickr
                                        className="form-control form-control-sm"
                                        placeholder="Select Date Range"
                                        options={{
                                            mode: "range",
                                            dateFormat: "Y-m-d"
                                        }}
                                        onChange={(dates) => {
                                            if (dates.length === 2) {
                                                const start = getLocalDateString(dates[0]);
                                                const end = getLocalDateString(dates[1]);
                                                setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
                                            } else if (dates.length === 0) {
                                                setFilters(prev => ({ ...prev, startDate: '', endDate: '' }));
                                            }
                                        }}
                                        style={{ width: '200px' }}
                                    />
                                    <button className="btn btn-soft-secondary btn-sm" onClick={() => { setFilters({ name: '', email: '', category: '', startDate: '', endDate: '', status: '' }); fetchLeads(1); }}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="card-body border border-dashed border-end-0 border-start-0">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger">{error}</div>
                        ) : (
                            <div className="table-responsive table-card mb-1">
                                <table className="table table-nowrap align-middle">
                                    <thead className="text-muted table-light">
                                        <tr className="text-uppercase">
                                            <th>SR No.</th>
                                            <th>Name</th>
                                            <th>Mobile</th>
                                            <th>Email</th>
                                            <th>Category</th>
                                            <th>City</th>
                                            <th>Lead Date</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="list form-check-all">
                                        {leads.length > 0 ? leads.map((lead, index) => (
                                            <tr key={lead.id}>
                                                <td className="id">{index + 1}</td>
                                                <td className="name">{lead.name}</td>
                                                <td className="mobile">{lead.mobile}</td>
                                                <td className="email">{lead.email || 'N/A'}</td>
                                                <td className="category">
                                                    <span className="text-capitalize">{lead.category.replace('_', ' ')}</span>
                                                </td>
                                                <td className="city">{lead.city || 'N/A'}</td>
                                                <td className="date">
                                                    {formatDate(lead.VendorLeads[0]?.created_at || lead.created_at)}
                                                </td>
                                                <td className="status">
                                                    {statusBadge(lead.VendorLeads[0]?.status || 'open')}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2">
                                                        <Link to={`/admin/my-leads/${lead.id}`} className="btn btn-soft-primary btn-sm">
                                                            View Details
                                                        </Link>
                                                        {lead.VendorLeads && lead.VendorLeads.length > 0 && lead.VendorLeads[0].is_unlocked === false && (
                                                            <button
                                                                className="btn btn-soft-warning btn-sm"
                                                                onClick={() => {
                                                                    Swal.fire({
                                                                        title: 'Unlock Lead?',
                                                                        text: "This will consume 1 lead balance from your account.",
                                                                        icon: 'warning',
                                                                        showCancelButton: true,
                                                                        confirmButtonColor: '#08a88a',
                                                                        cancelButtonColor: '#d33',
                                                                        confirmButtonText: 'Yes, unlock it!'
                                                                    }).then(async (result) => {
                                                                        if (result.isConfirmed) {
                                                                            try {
                                                                                const res = await apiClient.post(`/vendors/leads/${lead.id}/unlock`);
                                                                                if (res.data.success) {
                                                                                    Swal.fire(
                                                                                        'Unlocked!',
                                                                                        'The lead has been successfully unlocked.',
                                                                                        'success'
                                                                                    );
                                                                                    fetchLeads(pagination.page);
                                                                                }
                                                                            } catch (err) {
                                                                                Swal.fire(
                                                                                    'Error!',
                                                                                    err.response?.data?.message || "Failed to unlock lead",
                                                                                    'error'
                                                                                );
                                                                            }
                                                                        }
                                                                    });
                                                                }}
                                                            >
                                                                <i className="ri-lock-unlock-line align-middle me-1"></i> Unlock
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="9" className="text-center p-5">
                                                    <lord-icon src="https://cdn.lordicon.com/msoeawqm.json" trigger="loop" colors="primary:#121331,secondary:#08a88a" style={{ width: '75px', height: '75px' }}></lord-icon>
                                                    <h5 className="mt-2">Sorry! No Leads Found</h5>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {!loading && pagination.totalPages > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="text-muted">
                                    Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} leads
                                </div>
                                <nav aria-label="Page navigation">
                                    <ul className="pagination pagination-separated mb-0">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pagination.page - 1)}>Previous</button>
                                        </li>
                                        {[...Array(pagination.totalPages)].map((_, i) => (
                                            <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pagination.page + 1)}>Next</button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default MyLeads;
